/**
 * useCategoryCrossing Hook
 *
 * Detects when the revised score crosses a category boundary during
 * a rewrite session. Only celebrates IMPROVEMENTS (score going down).
 *
 * Category thresholds (0-10 backend scale):
 * - safe:          score <= 2.0
 * - caution:       score <= 5.0
 * - high_drama:    score <= 7.0
 * - extreme_drama: score > 7.0
 *
 * Celebration tiers (graduated to prevent fatigue):
 * 1. First crossing per session: full confetti + success haptic
 * 2. Subsequent crossings: subtle flash + medium haptic
 * 3. Never celebrate score INCREASES
 */

import { useRef, useState, useEffect } from 'react';

type DramaCategory = 'safe' | 'caution' | 'high_drama' | 'extreme_drama';

export interface CategoryCrossingState {
  /** Whether a celebration should fire */
  shouldCelebrate: boolean;
  /** The category that was reached (null if no celebration) */
  category: DramaCategory | null;
  /** Whether this is the first crossing in the session */
  isFirstCrossing: boolean;
}

/**
 * Map a 0-10 backend score to a drama category.
 */
function getCategory(score: number): DramaCategory {
  if (score <= 2.0) return 'safe';
  if (score <= 5.0) return 'caution';
  if (score <= 7.0) return 'high_drama';
  return 'extreme_drama';
}

/**
 * Numeric rank for category comparison. Lower = better.
 */
function categoryRank(cat: DramaCategory): number {
  const ranks: Record<DramaCategory, number> = {
    safe: 0,
    caution: 1,
    high_drama: 2,
    extreme_drama: 3,
  };
  return ranks[cat];
}

const NO_CELEBRATION: CategoryCrossingState = {
  shouldCelebrate: false,
  category: null,
  isFirstCrossing: false,
};

/**
 * Detect category boundary crossings during a rewrite session.
 *
 * @param currentScore - Current revised score (0-10 backend scale)
 * @param originalScore - Original score from initial analysis (0-10 backend scale)
 * @returns Celebration state indicating if/how to celebrate
 */
export function useCategoryCrossing(
  currentScore: number,
  originalScore: number,
): CategoryCrossingState {
  const crossingsRef = useRef(0);
  const prevCategoryRef = useRef<DramaCategory>(getCategory(originalScore));
  const [celebration, setCelebration] = useState<CategoryCrossingState>(NO_CELEBRATION);

  useEffect(() => {
    const currentCategory = getCategory(currentScore);
    const prevCategory = prevCategoryRef.current;

    // Only celebrate improvements (category rank going down = better)
    const improved = categoryRank(currentCategory) < categoryRank(prevCategory);

    if (improved) {
      crossingsRef.current++;
      prevCategoryRef.current = currentCategory;
      setCelebration({
        shouldCelebrate: true,
        category: currentCategory,
        isFirstCrossing: crossingsRef.current === 1,
      });
    } else {
      // Update ref for worsening but do not celebrate
      prevCategoryRef.current = currentCategory;
      setCelebration(NO_CELEBRATION);
    }
  }, [currentScore]);

  return celebration;
}
